import { getErrorMessage } from '@fruitful/fruitils';
import {
    type DepositAccount,
    RetirementContributionType,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    ControlledPicker,
    Form,
    FullScreenSpinner,
    isWeb,
    SafeAreaView,
    SmoothieScrollView,
    useToastController,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import SelectAccountSheet from '../../../banking/components/transfers/SelectAccountSheet';
import useGetAllBankAccountDetails from '../../../banking/hooks/useGetAllBankAccountDetails';
import useGetInternalAndExternalDepositAccounts from '../../../banking/hooks/useGetInternalAndExternalDepositAccounts';
import ControlledDepositAccountSelect from '../../../common/components/controlled/ControlledDepositAccountSelect';
import { assertNotNullish } from '../../../common/misc';
import { captureException, captureMessage } from '../../../logger/Logger';
import { useGetApexAccountDetails } from '../../hooks/useGetApexAccountDetails';
import useTestICTDeposit, {
    type TestICTDepositRequest,
} from '../../hooks/useTestICTDeposit';

const depositAccountSchema = z.object({
    id: z.string(),
    isExternal: z.boolean(),
    accountNumberMasked: z.string(),
    accountType: z.string(),
    bankName: z.string(),
    nickname: z.string(),
    availableBalance: z.number().optional(),
    imgBase64: z.string().optional(),
    logo: z.string().optional(),
    icon: z.string().optional(),
    color: z.string().optional(),
    accountNumberMaskedIsTokenized: z.boolean(),
    nonTokenizedAccountNumberMasked: z.string().optional(),
});

const contributionTypeOptions = Object.values(RetirementContributionType).map(
    (type) => ({
        label: type.replace(/_/g, ' '),
        value: type,
    }),
);

const currentYear = new Date().getFullYear();

const createIctDepositSchema = (isRetirementAccount: boolean) =>
    z
        .object({
            fromDepositAccount: depositAccountSchema.nullable(),
            toApexAccountId: z.string().min(1, 'Apex Account ID is required'),
            amountCents: z.string().min(1, 'Please enter an amount'),
            memo: z.string().optional(),
            taxYear: z.string().optional(),
            contributionType: z
                .nativeEnum(RetirementContributionType)
                .optional(),
        })
        .refine(({ fromDepositAccount }) => fromDepositAccount != null, {
            message: 'Please select an account',
            path: ['fromDepositAccount'],
        })
        .refine(
            ({ taxYear }) => !isRetirementAccount || (taxYear?.length ?? 0) > 0,
            {
                message: 'Tax year is required for retirement accounts',
                path: ['taxYear'],
            },
        )
        .refine(
            ({ contributionType }) =>
                !isRetirementAccount || contributionType != null,
            {
                message:
                    'Contribution type is required for retirement accounts',
                path: ['contributionType'],
            },
        );

type ICTDepositSchema = z.infer<ReturnType<typeof createIctDepositSchema>>;

const SimulateICTDeposit = () => {
    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const toast = useToastController();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSelectFromAccountSheetOpen, setIsSelectFromAccountSheetOpen] =
        useState(false);
    const [lastResponse, setLastResponse] = useState<string | null>(null);

    const { mutateAsync: testICTDeposit } = useTestICTDeposit();
    const { refetchAccountDetails } = useGetAllBankAccountDetails(false);

    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalAndExternalDepositAccounts({});

    const { data: apexAccountDetails, isLoading: isLoadingApexAccount } =
        useGetApexAccountDetails(apexAccountId ?? '');

    const isRetirementAccount =
        apexAccountDetails?.isRetirementAccount ?? false;

    // Filter to internal accounts only
    const internalAccounts = (depositAccounts ?? []).filter(
        (account) => !account.isExternal,
    );

    const {
        register,
        control,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
    } = useForm<ICTDepositSchema>({
        resolver: zodResolver(createIctDepositSchema(isRetirementAccount)),
        defaultValues: {
            fromDepositAccount: null,
            toApexAccountId: apexAccountId ?? '',
            amountCents: '',
            memo: '',
            taxYear: String(currentYear),
            contributionType: RetirementContributionType.REGULAR,
        },
    });

    const onSubmit: SubmitHandler<ICTDepositSchema> = async ({
        fromDepositAccount,
        toApexAccountId,
        amountCents,
        memo,
        taxYear,
        contributionType,
    }) => {
        if (fromDepositAccount == null) {
            return;
        }

        setIsSubmitting(true);

        toast.show('Submitting ICT deposit...', {
            duration: 3000,
        });

        try {
            const request: TestICTDepositRequest = {
                fromSyncteraAccountId: assertNotNullish(fromDepositAccount.id),
                toApexAccountId: assertNotNullish(toApexAccountId),
                amountCents: parseInt(amountCents, 10),
                memo,
                ...(isRetirementAccount &&
                    taxYear != null &&
                    contributionType != null && {
                        retirementContribution: {
                            taxYear: parseInt(taxYear, 10),
                            type: contributionType,
                        },
                    }),
            };

            const response = await testICTDeposit({
                body: request,
            });

            setLastResponse(JSON.stringify(response, null, 2));

            const successMessage =
                response.stateMessage != null && response.stateMessage !== ''
                    ? `ICT deposit simulated: ${response.stateMessage}`
                    : 'ICT deposit simulated successfully';
            toast.show(successMessage, {
                duration: 10000,
            });
            captureMessage(
                `ICT deposit simulated: ${JSON.stringify(response)}`,
            );

            // Refresh account details
            void refetchAccountDetails();
        } catch (e) {
            captureException(e);
            setLastResponse(
                JSON.stringify({ error: getErrorMessage(e) }, null, 2),
            );
            toast.show(`Error! ${getErrorMessage(e)}`, {
                duration: 10000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (apexAccountId == null) {
        captureException(
            new Error(
                'Missing apexAccountId query param for ICT deposit simulation',
            ),
        );
        router.back();
        return null;
    }

    if (isLoadingDepositAccounts || isLoadingApexAccount) {
        return <FullScreenSpinner />;
    }

    const handleSelectFromAccount = (account: DepositAccount) => {
        setValue('fromDepositAccount', account, { shouldValidate: true });
    };

    return (
        <SafeAreaView>
            <SmoothieScrollView>
                <View>
                    <BodyLargeEmphasized alignSelf="center">
                        Simulate ICT Deposit
                    </BodyLargeEmphasized>
                    <View marginTop={-26} marginBottom={24}>
                        <BackButton
                            fallbackRoute="/home/investment"
                            iconType={BackButtonIconType.ARROW_LEFT}
                        />
                    </View>
                </View>
                <YStack
                    maxWidth={700}
                    alignSelf="center"
                    marginTop={isWeb ? 24 : 0}
                    marginHorizontal={16}
                >
                    <View marginBottom={16}>
                        <Form onSubmit={handleSubmit(onSubmit)} flex={1}>
                            <YStack gap={12} flex={1} minWidth={500}>
                                <ControlledDepositAccountSelect
                                    name="fromDepositAccount"
                                    label="From"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    placeholder="Select your account"
                                    onPress={() => {
                                        setIsSelectFromAccountSheetOpen(true);
                                    }}
                                />
                                <ControlledInput
                                    name="toApexAccountId"
                                    label={`To (Apex Account)${apexAccountDetails?.nickname != null ? ` - ${apexAccountDetails.nickname}` : ''}${apexAccountDetails?.registrationType != null ? ` / ${apexAccountDetails.registrationType}` : ''}`}
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    editable={false}
                                    disabled
                                />
                                <ControlledInput
                                    name="amountCents"
                                    label="Amount (cents)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    type="numeric"
                                    inputMode="numeric"
                                />
                                <ControlledInput
                                    name="memo"
                                    label="Memo"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                />

                                <ControlledInput
                                    name="taxYear"
                                    label="Tax Year (Retirement)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    type="numeric"
                                    inputMode="numeric"
                                    disabled={!isRetirementAccount}
                                />

                                <ControlledPicker
                                    name="contributionType"
                                    label="Contribution Type (Retirement)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    options={contributionTypeOptions}
                                    disabled={!isRetirementAccount}
                                />

                                <Button
                                    tone="positive"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    text="Simulate ICT deposit"
                                    onPress={handleSubmit(onSubmit)}
                                    disabled={!isValid || isSubmitting}
                                />

                                {lastResponse != null && (
                                    <View
                                        backgroundColor="$gray100"
                                        padding={12}
                                        borderRadius={8}
                                        marginTop={16}
                                    >
                                        <BodyLargeEmphasized marginBottom={8}>
                                            Response:
                                        </BodyLargeEmphasized>
                                        <BodySmallRegular
                                            fontFamily="$monoBody"
                                            whiteSpace="pre-wrap"
                                        >
                                            {lastResponse}
                                        </BodySmallRegular>
                                    </View>
                                )}
                            </YStack>
                        </Form>
                    </View>

                    <SelectAccountSheet
                        isOpen={isSelectFromAccountSheetOpen}
                        depositAccounts={internalAccounts}
                        onClose={() => {
                            setIsSelectFromAccountSheetOpen(false);
                        }}
                        onPlaidExit={() => {
                            setIsSelectFromAccountSheetOpen(false);
                        }}
                        onSelectAccount={handleSelectFromAccount}
                        enableLinkingNewAccount={false}
                        onNeedsReview={() => {}}
                    />
                </YStack>
            </SmoothieScrollView>
        </SafeAreaView>
    );
};

export default SimulateICTDeposit;
